import { format } from 'date-fns'
import { useNavigate } from 'react-router'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { Phenotype } from '@/api/types'

interface PhenotypeCardProps {
  phenotype: Phenotype
}

/**
 * 5-state badge map for extractor status.
 * Exported so PhenotypeDetail can import and reuse without duplication.
 */
export const STATUS_MAP: Record<string, { label: string; variant: 'success' | 'warning' | 'default' | 'secondary' }> = {
  active:     { label: 'Active',       variant: 'success'   },
  stale:      { label: 'Stale',        variant: 'warning'   },
  generating: { label: 'Generating',   variant: 'default'   },
  pending:    { label: 'Pending',      variant: 'secondary' },
  none:       { label: 'No Extractor', variant: 'secondary' },
}

export function PhenotypeCard({ phenotype }: PhenotypeCardProps) {
  const navigate = useNavigate()

  const {
    carrierName,
    id,
    detectionThreshold,
    carrierId,
    lastUpdated,
  } = phenotype

  // Format phenotype ID to show first 8 chars + "..."
  const displayId = id.length > 8 ? `${id.slice(0, 8)}...` : id

  // Format threshold as percentage
  const thresholdPercent = `${Math.round(detectionThreshold * 100)}%`

  // Determine status badge (5-state from backend extractorStatus)
  const status = STATUS_MAP[phenotype.extractorStatus ?? 'none'] ?? STATUS_MAP.none

  const handleViewRuns = () => {
    navigate(`/runs?phenotype=${id}`)
  }

  const handleViewDetail = () => {
    navigate(`/phenotypes/${id}`)
  }

  return (
    <Card className="transition-colors hover:border-primary/50">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle>{carrierName}</CardTitle>
            <CardDescription className="font-mono text-xs">
              {displayId}
            </CardDescription>
          </div>
          <Badge variant={status.variant}>{status.label}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Detection Threshold</span>
          <span className="font-medium">{thresholdPercent}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Carrier ID</span>
          <span className="font-mono text-xs">{carrierId}</span>
        </div>
        {/* Statistics */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Extractions</span>
          <span className="font-medium">{phenotype.statistics?.extractionCount ?? 0}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Avg Confidence</span>
          <span className="font-medium">
            {phenotype.statistics?.averageConfidence
              ? `${Math.round(phenotype.statistics.averageConfidence * 100)}%`
              : 'N/A'}
          </span>
        </div>
        {lastUpdated && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Last Updated</span>
            <span className="text-xs">
              {format(new Date(lastUpdated), 'MMM d, yyyy')}
            </span>
          </div>
        )}
        <div className="flex gap-2 mt-2">
          <Button
            variant="default"
            size="sm"
            onClick={handleViewDetail}
            className="flex-1"
          >
            View Detail
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewRuns}
            className="flex-1"
          >
            View Runs
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
